import React, { useState, useEffect } from 'react';
import GraphqlService from '../../service/graphqlService';
import { AUDIT_ENTITY_CERTIFICATE_PROVISIONING, AUDIT_CHANGE_QUOTA_INCREASE } from '../../hooks/useAuditLog';
import useAnalytics from '../../hooks/useAnalytics';

const IncreaseQuotaModal = ({ isOpen, onClose, credential, onIncrease }) => {
  const [increaseBy, setIncreaseBy] = useState(500); // Default value
  const [errorMessage, setErrorMessage] = useState(''); // Added for displaying error messages
  const [successMessage, setSuccessMessage] = useState(''); // Added for success notification
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setAnalytics } = useAnalytics();

  useEffect(() => {
    if (isOpen) {
      // Reset to a default or last used sensible value when modal opens, if desired
      setIncreaseBy(500);
      setErrorMessage(''); // Reset error message when modal opens
      setSuccessMessage(''); // Reset success message when modal opens

      // Remove any existing popup if present
      const existingPopup = document.getElementById('quota-success-popup');
      if (existingPopup) {
        existingPopup.remove();
      }

      // Clean up the global function if it exists
      if (window.handleQuotaPopupClose) {
        window.handleQuotaPopupClose = null;
      }
    }

    // Cleanup function that runs when the component unmounts
    return () => {
      // Remove any existing popup if present
      const existingPopup = document.getElementById('quota-success-popup');
      if (existingPopup) {
        existingPopup.remove();
      }

      // Clean up the global function
      if (window.handleQuotaPopupClose) {
        window.handleQuotaPopupClose = null;
      }
    };
  }, [isOpen]);

  if (!isOpen || !credential) return null;

  const handleCancel = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-increase_quota-cancel' }
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-increase_quota-confirm' }
    });

    const amount = parseInt(increaseBy, 10);
    if (!isNaN(amount) && amount > 0) {
      setIsSubmitting(true);
      try {
        // Ensure threshold is always a valid number, defaulting to 0 if null/undefined
        const threshold = credential.quotaData?.threshold !== null && credential.quotaData?.threshold !== undefined 
          ? credential.quotaData?.threshold 
          : 0;
        await GraphqlService.updateCertificateQuota(credential.userId, amount, threshold);


        // Call onIncrease with all the information needed for audit logging
        onIncrease(credential.userId, amount, {
          success: true,
          credential: credential,
          auditInfo: {
            entity: AUDIT_ENTITY_CERTIFICATE_PROVISIONING,
            change: AUDIT_CHANGE_QUOTA_INCREASE,
            entityId: credential.id,
            description: 'Remaining Quota',
            logicalUser: credential.logicalUser || credential.id,
            value: String(amount)
          }
        });

        setErrorMessage(''); // Clear error message on successful submission
        setSuccessMessage('Quota increased successfully.');
        setIsSubmitting(false);

        // Store the onClose function for later use
        const closeModalFunc = onClose;

        // Force show the popup by adding it directly to the DOM
        const popupDiv = document.createElement('div');
        popupDiv.id = 'quota-success-popup';
        document.body.appendChild(popupDiv);

        // Render the popup using DOM directly
        const successPopup = document.getElementById('quota-success-popup');
        if (successPopup) {
          // Create a function to handle popup close
          const handlePopupClose = function () {
            document.getElementById('quota-success-popup').remove();
            // Call the onClose function to close the main modal
            closeModalFunc();
          };

          // Add the handlePopupClose function to the window object so it can be accessed by the onclick
          window.handleQuotaPopupClose = handlePopupClose;

          successPopup.innerHTML = `
            <div style="position:fixed;top:0;left:0;right:0;bottom:0;background-color:rgba(0,0,0,0.75);z-index:9999;display:flex;justify-content:center;align-items:center;">
              <div style="background-color:white;padding:24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);max-width:420px;width:100%;">
                <div style="text-align:center;margin-bottom:24px;">
                  <svg style="margin:0 auto;height:48px;width:48px;color:#10B981;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 style="margin-top:8px;font-size:18px;font-weight:500;color:#111827;">Success!</h3>
                  <p style="margin-top:8px;font-size:14px;color:#6B7280;">
                    Quota increased successfully!
                  </p>
                </div>
                <button 
                  onclick="window.handleQuotaPopupClose();"
                  style="width:100%;padding:8px;font-size:14px;font-weight:500;color:white;background-color:#10B981;border:none;border-radius:4px;cursor:pointer;"
                >
                  OK
                </button>
              </div>
            </div>
          `;
        }
      } catch (error) {
        setErrorMessage('Failed to increase quota. Please try again.');
        setIsSubmitting(false);
      }
    } else {
      setErrorMessage("Quota increase must be higher than 0."); // Set error message
    }
  };

  // Just return the modal, popup is handled via direct DOM manipulation
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">Increase Certificate Quota</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="credentialName" className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              id="credentialName"
              value={credential.logicalUser || 'N/A'}
              disabled
              className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm text-gray-500"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="remainingQuota" className="block text-sm font-medium text-gray-700">Remaining Quota</label>
            <input
              type="text"
              id="remainingQuota"
              value={(credential.quotaData?.quota || 0).toLocaleString()}
              disabled
              className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm text-gray-500"
            />
          </div>
          <div className="mb-6">
            <label htmlFor="increaseBy" className="block text-sm font-medium text-gray-700">Increase by</label>
            <input
              type="number"
              id="increaseBy"
              value={increaseBy}
              onChange={(e) => setIncreaseBy(e.target.value)}
              min="1"
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {errorMessage && <p className="text-red-500 text-xs mt-1">{errorMessage}</p>} {/* Display error message */}
          </div>          {successMessage && <div className="mb-4 p-3 text-sm text-green-700 bg-green-100 rounded-md" role="alert">{successMessage}</div>}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSubmitting ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div> : 'Increase'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncreaseQuotaModal;
